import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPut } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------
const IconSave = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);
const IconPlus = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);
const IconTrash = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);
const IconGrip = () => (
  <svg className="w-4 h-4 text-gray-500 cursor-grab" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
  </svg>
);
const IconReset = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);
const IconDiscord = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
  </svg>
);
const IconHelp = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
const IconX = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);
const IconCheck = () => (
  <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

// ---------------------------------------------------------------------------
// Shared UI helpers
// ---------------------------------------------------------------------------
function ConfirmDialog({ title, message, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl p-6 w-full max-w-sm">
        <div className="text-2xl mb-3">🗑️</div>
        <h3 className="text-base font-semibold text-gray-100 mb-1">{title}</h3>
        <p className="text-sm text-gray-400 mb-5">{message}</p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-500 rounded-lg transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function Toast({ message, type, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 3500);
    return () => clearTimeout(t);
  }, [onDismiss]);
  const colors = type === 'success'
    ? 'bg-green-900/90 border-green-600 text-green-200'
    : 'bg-red-900/90 border-red-600 text-red-200';
  return (
    <div className={`fixed bottom-6 right-6 z-[200] flex items-center gap-3 px-4 py-3 rounded-xl border shadow-xl backdrop-blur ${colors} animate-fadeIn`}>
      <span>{type === 'success' ? '✓' : '✕'}</span>
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onDismiss} className="ml-2 opacity-60 hover:opacity-100 text-xs">✕</button>
    </div>
  );
}

function SectionCard({ title, icon, children, action }) {
  return (
    <div className="bg-gray-800/60 border border-gray-700 rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700/60 bg-gray-800/40">
        <div className="flex items-center gap-2.5">
          <span className="text-lg">{icon}</span>
          <h3 className="font-semibold text-gray-100 text-base">{title}</h3>
        </div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function DiscordHelpModal({ onClose }) {
  const openBotInvite = () => {
    window.open('https://discord.com/oauth2/authorize?client_id=1490638575593848872&permissions=2252077308922880&integration_type=0&scope=bot+applications.commands', '_blank');
  };

  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 bg-gray-800/80">
          <div className="flex items-center gap-3">
            <span className="text-2xl">📘</span>
            <h2 className="text-lg font-semibold text-gray-100">Discord Setup Guide</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <IconX />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto p-6 space-y-6">
          {/* Overview */}
          <section>
            <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wide mb-2">Features Overview</h3>
            <ul className="text-sm text-gray-300 space-y-1">
              <li className="flex items-start gap-2"><IconCheck /> <span><strong>/link</strong> — Connect your Discord to your CoC account for bonus points</span></li>
              <li className="flex items-start gap-2"><IconCheck /> <span><strong>War Reminders</strong> — Automatic reminders when attacks are remaining</span></li>
              <li className="flex items-start gap-2"><IconCheck /> <span><strong>Hall of Fame</strong> — End-of-season rankings posted automatically</span></li>
              <li className="flex items-start gap-2"><IconCheck /> <span><strong>Role Assignment</strong> — Auto-assign roles when members link their account</span></li>
            </ul>
          </section>

          {/* Roles */}
          <section>
            <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wide mb-2">Recommended Roles</h3>
            <div className="space-y-2 text-sm">
              <div className="bg-gray-900/50 rounded-lg p-3">
                <div className="font-medium text-gray-200">@everyone</div>
                <div className="text-gray-400 text-xs">Only <strong>/link</strong> command enabled. Disable all other bot permissions.</div>
              </div>
              <div className="bg-gray-900/50 rounded-lg p-3">
                <div className="font-medium text-green-400">Member</div>
                <div className="text-gray-400 text-xs">Granted after linking. Access to war reminders and hall of fame channels.</div>
              </div>
              <div className="bg-gray-900/50 rounded-lg p-3">
                <div className="font-medium text-blue-400">Admin</div>
                <div className="text-gray-400 text-xs">Full bot command access (/accept, /status, etc.) and admin channel access.</div>
              </div>
              <p className="text-xs text-gray-500 italic">Add clan-specific roles (Elder, Co-Leader) as needed for your server structure.</p>
            </div>
          </section>

          {/* Channels */}
          <section>
            <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wide mb-2">Recommended Channels</h3>
            <div className="grid gap-2 text-sm">
              <div className="flex items-center gap-3 bg-gray-900/50 rounded-lg p-3">
                <span className="text-lg">👋</span>
                <div className="flex-1">
                  <div className="font-medium text-gray-200">#welcome</div>
                  <div className="text-gray-400 text-xs">/link commands, join requests — visible to <strong>@everyone</strong></div>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-gray-900/50 rounded-lg p-3">
                <span className="text-lg">⚔️</span>
                <div className="flex-1">
                  <div className="font-medium text-gray-200">#war-reminders</div>
                  <div className="text-gray-400 text-xs">Attack reminders — <strong>Member</strong> role only</div>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-gray-900/50 rounded-lg p-3">
                <span className="text-lg">🏆</span>
                <div className="flex-1">
                  <div className="font-medium text-gray-200">#hall-of-fame</div>
                  <div className="text-gray-400 text-xs">Season rankings — <strong>Member</strong> role only</div>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-gray-900/50 rounded-lg p-3">
                <span className="text-lg">🔒</span>
                <div className="flex-1">
                  <div className="font-medium text-gray-200">#admin</div>
                  <div className="text-gray-400 text-xs">Admin notifications — <strong>Admin</strong> role only</div>
                </div>
              </div>
            </div>
          </section>

          {/* Setup Steps */}
          <section>
            <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wide mb-2">Setup Steps</h3>
            <div className="space-y-3 text-sm">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">1</div>
                <div>
                  <div className="font-medium text-gray-200">Enable Developer Mode in Discord</div>
                  <div className="text-gray-400 text-xs">User Settings → Advanced → Developer Mode (ON)</div>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">2</div>
                <div>
                  <div className="font-medium text-gray-200">Invite the Bot</div>
                  <div className="text-gray-400 text-xs mb-2">Click the button below to add the bot to your server with required permissions.</div>
                  <button
                    onClick={openBotInvite}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#5865F2] hover:bg-[#4752C4] text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    <IconDiscord />
                    Add Bot to Server
                  </button>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">3</div>
                <div>
                  <div className="font-medium text-gray-200">Copy IDs to this page</div>
                  <div className="text-gray-400 text-xs">Right-click your server → Copy Server ID. Right-click channels → Copy Channel ID.</div>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-700 bg-gray-800/80 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg text-sm font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

const POINT_MIN = -200;
const POINT_MAX = 200;
const DONATION_MIN = 1;
const DONATION_MAX = 10000;

function PointInput({ label, description, value, onChange, min, max }) {
  const lo = min ?? POINT_MIN;
  const hi = max ?? POINT_MAX;
  const numVal = Math.min(hi, Math.max(lo, Number(value) || 0));
  const color = numVal > 0 ? 'text-green-400' : numVal < 0 ? 'text-red-400' : 'text-gray-400';
  const clamp = (v) => Math.min(hi, Math.max(lo, v));
  return (
    <div className="flex items-center justify-between gap-4 py-2.5 border-b border-gray-700/40 last:border-0">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-200">{label}</div>
        {description && <div className="text-xs text-gray-500 mt-0.5">{description}</div>}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={() => onChange(clamp(numVal - 1))}
          disabled={numVal <= lo}
          className="w-7 h-7 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed text-gray-300 flex items-center justify-center text-sm font-bold transition-colors"
        >−</button>
        <input
          type="number"
          value={numVal}
          min={lo}
          max={hi}
          onChange={e => onChange(clamp(parseInt(e.target.value, 10) || 0))}
          className="w-16 text-center bg-gray-900 border border-gray-600 rounded-lg py-1 text-sm font-mono font-bold focus:outline-none focus:ring-1 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
        <button
          onClick={() => onChange(clamp(numVal + 1))}
          disabled={numVal >= hi}
          className="w-7 h-7 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed text-gray-300 flex items-center justify-center text-sm font-bold transition-colors"
        >+</button>
        <span className={`w-10 text-right text-sm font-bold font-mono ${color}`}>
          {numVal >= 0 ? `+${numVal}` : `${numVal}`}
        </span>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, label, description }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-gray-700/40 last:border-0">
      <div className="flex-1">
        <div className="text-sm font-medium text-gray-200">{label}</div>
        {description && <div className="text-xs text-gray-500 mt-0.5">{description}</div>}
      </div>
      <button
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${checked ? 'bg-blue-600' : 'bg-gray-600'}`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
      </button>
    </div>
  );
}

const _SNOWFLAKE_RE = /^\d{17,20}$/;
const _INVITE_RE = /^https:\/\/(discord\.gg|discord\.com\/invite|discordapp\.com\/invite)\/[A-Za-z0-9\-]+$/;

function TextInput({ label, description, value, onChange, placeholder, prefix, monospace, maxLength, validate }) {
  const displayVal = value || '';
  const error = validate ? validate(displayVal) : null;
  return (
    <div className="flex flex-col gap-1.5 py-2.5 border-b border-gray-700/40 last:border-0">
      <div>
        <div className="text-sm font-medium text-gray-200">{label}</div>
        {description && <div className="text-xs text-gray-500 mt-0.5">{description}</div>}
      </div>
      <div className="flex items-center gap-2">
        {prefix && <span className="text-gray-500 text-sm">{prefix}</span>}
        <input
          type="text"
          value={displayVal}
          onChange={e => onChange(maxLength ? e.target.value.slice(0, maxLength) : e.target.value)}
          placeholder={placeholder || ''}
          maxLength={maxLength}
          className={`flex-1 bg-gray-900 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 placeholder-gray-600 ${monospace ? 'font-mono' : ''} ${error ? 'border-red-500 focus:ring-red-500' : 'border-gray-600 focus:ring-blue-500'}`}
        />
      </div>
      {error && <div className="text-xs text-red-400 mt-0.5">{error}</div>}
    </div>
  );
}

function validateSnowflake(val) {
  if (!val) return null;
  if (!/^\d+$/.test(val)) return 'Must contain only digits';
  if (val.length < 17 || val.length > 20) return 'Discord IDs are 17–20 digits';
  return null;
}

function validateInviteLink(val) {
  if (!val) return null;
  if (!_INVITE_RE.test(val)) return 'Must be a valid Discord invite (https://discord.gg/...)';
  return null;
}

function SelectInput({ label, description, value, onChange, options }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5 border-b border-gray-700/40 last:border-0">
      <div className="flex-1">
        <div className="text-sm font-medium text-gray-200">{label}</div>
        {description && <div className="text-xs text-gray-500 mt-0.5">{description}</div>}
      </div>
      <select
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className="bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-200"
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function SaveButton({ onClick, loading, dirty }) {
  return (
    <button
      onClick={onClick}
      disabled={loading || !dirty}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
        dirty
          ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-900/30'
          : 'bg-gray-700 text-gray-500 cursor-not-allowed'
      }`}
    >
      {loading ? (
        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
      ) : (
        <IconSave />
      )}
      {loading ? 'Saving…' : 'Save'}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Tab: Scoring Points
// ---------------------------------------------------------------------------
function ScoringTab({ slug, onToast, t }) {
  const queryClient = useQueryClient();

  const { data: defaults } = useQuery({
    queryKey: ['settings_defaults', slug],
    queryFn: () => apiGet(`/v1/clan/${slug}/settings/defaults`, { withAuth: true }),
    staleTime: Infinity,
  });

  const { data: savedPoints, isLoading } = useQuery({
    queryKey: ['settings_points', slug],
    queryFn: () => apiGet(`/v1/clan/${slug}/settings/points`, { withAuth: true }),
    staleTime: 30_000,
  });

  const [points, setPoints] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (savedPoints && !dirty) setPoints({ ...savedPoints });
  }, [savedPoints]);

  const mutation = useMutation({
    mutationFn: (data) => apiPut(`/v1/clan/${slug}/settings/points`, data, { withAuth: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings_points', slug] });
      queryClient.invalidateQueries({ queryKey: ['data', slug] });
      setDirty(false);
      onToast('Scoring settings saved!', 'success');
    },
    onError: (err) => onToast(`Save failed: ${err.message}`, 'error'),
  });

  const set = useCallback((key, val) => {
    setPoints(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  }, []);

  const handleReset = () => {
    if (!defaults?.points) return;
    setPoints({ ...defaults.points });
    setDirty(true);
  };

  if (isLoading || !points) {
    return <div className="flex items-center justify-center h-48 text-gray-400">{t ? t('loading') : 'Loading…'}</div>;
  }

  return (
    <div className="space-y-5">
      {/* CW Attack Points */}
      <SectionCard title={t ? t('settings_scoring_cw_attack') : 'Clan War – Attack Points'} icon="⚔️" action={
        <div className="flex gap-2">
          <button onClick={handleReset} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-400 hover:text-gray-200 bg-gray-700/60 hover:bg-gray-700 rounded-lg transition-colors">
            <IconReset /><span>{t ? t('settings_reset_defaults') : 'Reset defaults'}</span>
          </button>
          <SaveButton onClick={() => mutation.mutate(points)} loading={mutation.isPending} dirty={dirty} />
        </div>
      }>
        <div className="mb-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t ? t('settings_stars_3') : '3 Stars'}</div>
        <PointInput label={t ? t('settings_vs_higher_th') : 'vs Higher TH'} description={t ? t('settings_vs_higher_th_desc') : 'Attack a higher town hall and get 3 stars'} value={points.cw_3_stars_up} onChange={v => set('cw_3_stars_up', v)} />
        <PointInput label={t ? t('settings_vs_same_th') : 'vs Same TH'} value={points.cw_3_stars_same} onChange={v => set('cw_3_stars_same', v)} />
        <PointInput label={t ? t('settings_vs_lower_th') : 'vs Lower TH'} value={points.cw_3_stars_down} onChange={v => set('cw_3_stars_down', v)} />
        <div className="mt-4 mb-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t ? t('settings_stars_2') : '2 Stars'}</div>
        <PointInput label={t ? t('settings_vs_higher_th') : 'vs Higher TH'} value={points.cw_2_stars_up} onChange={v => set('cw_2_stars_up', v)} />
        <PointInput label={t ? t('settings_vs_same_th') : 'vs Same TH'} value={points.cw_2_stars_same} onChange={v => set('cw_2_stars_same', v)} />
        <PointInput label={t ? t('settings_vs_lower_th') : 'vs Lower TH'} value={points.cw_2_stars_down} onChange={v => set('cw_2_stars_down', v)} />
        <div className="mt-4 mb-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t ? t('settings_stars_1') : '1 Star'}</div>
        <PointInput label={t ? t('settings_vs_higher_th') : 'vs Higher TH'} value={points.cw_1_star_up} onChange={v => set('cw_1_star_up', v)} />
        <PointInput label={t ? t('settings_vs_same_th') : 'vs Same TH'} value={points.cw_1_star_same} onChange={v => set('cw_1_star_same', v)} />
        <PointInput label={t ? t('settings_vs_lower_th') : 'vs Lower TH'} value={points.cw_1_star_down} onChange={v => set('cw_1_star_down', v)} />
        <div className="mt-4 mb-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t ? t('settings_stars_0_penalty') : '0 Stars / Penalty'}</div>
        <PointInput label={t ? t('settings_0_stars_any_th') : '0 Stars (any TH)'} value={points.cw_0_stars} onChange={v => set('cw_0_stars', v)} />
        <PointInput label={t ? t('settings_missed_penalty') : 'Missed Attack Penalty'} description={t ? t('settings_missed_penalty_cw_desc') : 'Applied per missed attack in CW'} value={points.cw_missed_attack_penalty} onChange={v => set('cw_missed_attack_penalty', v)} />
      </SectionCard>

      {/* CW Defense Points */}
      <SectionCard title={t ? t('settings_scoring_cw_defense') : 'Clan War – Defense Points'} icon="🛡️">
        <PointInput label={t ? t('settings_def_0_stars') : '0 Stars conceded'} description={t ? t('settings_def_0_stars_desc') : 'Successfully defended — no stars given'} value={points.defense_0_stars} onChange={v => set('defense_0_stars', v)} />
        <PointInput label={t ? t('settings_def_1_star') : '1 Star conceded'} value={points.defense_1_star} onChange={v => set('defense_1_star', v)} />
        <PointInput label={t ? t('settings_def_2_stars') : '2 Stars conceded'} value={points.defense_2_stars} onChange={v => set('defense_2_stars', v)} />
        <PointInput label={t ? t('settings_def_3_stars') : '3 Stars conceded'} description={t ? t('settings_def_3_stars_desc') : 'Full destruction of your base'} value={points.defense_3_stars} onChange={v => set('defense_3_stars', v)} />
      </SectionCard>

      {/* CWL Attack Points */}
      <SectionCard title={t ? t('settings_scoring_cwl_attack') : 'Clan War League – Attack Points'} icon="🏆">
        <div className="mb-3 text-xs text-gray-500">{t ? t('settings_cwl_flat_note') : 'CWL uses flat points regardless of TH difference'}</div>
        <PointInput label={t ? t('settings_stars_3') : '3 Stars'} value={points.cwl_3_stars} onChange={v => set('cwl_3_stars', v)} />
        <PointInput label={t ? t('settings_stars_2') : '2 Stars'} value={points.cwl_2_stars} onChange={v => set('cwl_2_stars', v)} />
        <PointInput label={t ? t('settings_stars_1') : '1 Star'} value={points.cwl_1_star} onChange={v => set('cwl_1_star', v)} />
        <PointInput label={t ? t('settings_stars_0') : '0 Stars'} value={points.cwl_0_stars} onChange={v => set('cwl_0_stars', v)} />
        <PointInput label={t ? t('settings_missed_penalty') : 'Missed Attack Penalty'} description={t ? t('settings_missed_penalty_cwl_desc') : 'Applied per missed attack in CWL'} value={points.cwl_missed_attack_penalty} onChange={v => set('cwl_missed_attack_penalty', v)} />
      </SectionCard>

      {/* Bonus Points */}
      <SectionCard title={t ? t('settings_scoring_bonus') : 'Bonus Points'} icon="➕">
        <PointInput
          label={t ? t('settings_discord_bonus') : 'Discord Member Bonus'}
          description={t ? t('settings_discord_bonus_desc') : 'Awarded per season for linked Discord members'}
          value={points.discord_member_bonus}
          onChange={v => set('discord_member_bonus', v)}
          min={0}
          max={999}
        />
        <TextInput
          label={t ? t('settings_donation_milestone') : 'Donation Milestone'}
          description={t ? t('settings_donation_milestone_desc') : 'Points awarded once per each donation milestone reached'}
          value={String(points.donation_milestone)}
          onChange={v => {
            const n = parseInt(v, 10);
            if (!isNaN(n) && n > 0) set('donation_milestone', n);
          }}
          placeholder="1000"
          monospace
        />
      </SectionCard>

      <div className="flex justify-end">
        <SaveButton onClick={() => mutation.mutate(points)} loading={mutation.isPending} dirty={dirty} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Clan Rules
// ---------------------------------------------------------------------------
function RulesTab({ slug, onToast, t }) {
  const queryClient = useQueryClient();
  const [topics, setTopics] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const { data: savedRules, isLoading } = useQuery({
    queryKey: ['settings_rules', slug],
    queryFn: () => apiGet(`/v1/clan/${slug}/settings/rules`, { withAuth: true }),
    staleTime: 30_000,
  });

  useEffect(() => {
    if (savedRules && !dirty) setTopics(JSON.parse(JSON.stringify(savedRules)));
  }, [savedRules]);

  const mutation = useMutation({
    mutationFn: (data) => apiPut(`/v1/clan/${slug}/settings/rules`, data, { withAuth: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings_rules', slug] });
      queryClient.invalidateQueries({ queryKey: ['rules', slug] });
      setDirty(false);
      onToast('Clan rules saved!', 'success');
    },
    onError: (err) => onToast(`Save failed: ${err.message}`, 'error'),
  });

  const updateTopic = (idx, field, value) => {
    setTopics(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
    setDirty(true);
  };

  const addTopic = () => {
    if (topics.length >= 10) return;
    setTopics(prev => [...prev, {
      category_key: `topic_${Date.now()}`,
      title: 'New Topic',
      icon: '📋',
      sort_order: prev.length,
      entries: [],
    }]);
    setDirty(true);
  };

  const removeTopic = (idx) => {
    setConfirm({
      title: 'Delete topic?',
      message: `"${topics[idx]?.title || 'This topic'}" and all its rules will be permanently deleted.`,
      onConfirm: () => {
        setTopics(prev => prev.filter((_, i) => i !== idx));
        setDirty(true);
        setConfirm(null);
      },
    });
  };

  const addEntry = (topicIdx) => {
    if (topics[topicIdx].entries.length >= 15) return;
    setTopics(prev => {
      const next = [...prev];
      const entries = [...next[topicIdx].entries, {
        label: 'New Rule',
        content: '',
        sort_order: next[topicIdx].entries.length,
      }];
      next[topicIdx] = { ...next[topicIdx], entries };
      return next;
    });
    setDirty(true);
  };

  const updateEntry = (topicIdx, entryIdx, field, value) => {
    setTopics(prev => {
      const next = [...prev];
      const entries = [...next[topicIdx].entries];
      entries[entryIdx] = { ...entries[entryIdx], [field]: value };
      next[topicIdx] = { ...next[topicIdx], entries };
      return next;
    });
    setDirty(true);
  };

  const removeEntry = (topicIdx, entryIdx) => {
    const label = topics[topicIdx]?.entries[entryIdx]?.label || 'This rule';
    setConfirm({
      title: 'Delete rule?',
      message: `"${label}" will be permanently deleted.`,
      onConfirm: () => {
        setTopics(prev => {
          const next = [...prev];
          const entries = next[topicIdx].entries.filter((_, i) => i !== entryIdx);
          next[topicIdx] = { ...next[topicIdx], entries };
          return next;
        });
        setDirty(true);
        setConfirm(null);
      },
    });
  };

  const handleSave = () => {
    const payload = topics.map((topic, i) => ({
      ...topic,
      sort_order: i,
      entries: topic.entries.map((e, j) => ({ ...e, sort_order: j })),
    }));
    mutation.mutate(payload);
  };

  if (isLoading || !topics) {
    return <div className="flex items-center justify-center h-48 text-gray-400">{t ? t('loading') : 'Loading…'}</div>;
  }

  return (
    <div className="space-y-4">
      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-400">
          {t ? t('settings_rules_limit') : 'Max 10 topics, each with up to 15 rules.'}
        </p>
        <SaveButton onClick={handleSave} loading={mutation.isPending} dirty={dirty} />
      </div>

      {topics.map((topic, ti) => (
        <div key={ti} className="bg-gray-800/60 border border-gray-700 rounded-2xl overflow-hidden">
          {/* Topic Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-gray-800/40 border-b border-gray-700/60">
            <IconGrip />
            <input
              type="text"
              value={topic.icon || ''}
              onChange={e => updateTopic(ti, 'icon', e.target.value.slice(0, 2))}
              placeholder="📋"
              maxLength={2}
              className="w-10 bg-gray-900/60 border border-gray-600 rounded-lg px-2 py-1 text-center text-base focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <input
              type="text"
              value={topic.title}
              onChange={e => updateTopic(ti, 'title', e.target.value.slice(0, 30))}
              placeholder="Topic title"
              maxLength={30}
              className="flex-1 bg-gray-900/60 border border-gray-600 rounded-lg px-3 py-1.5 text-sm font-semibold text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <span className="text-xs text-gray-500">{topic.entries.length}/15 rules</span>
            <button
              onClick={() => removeTopic(ti)}
              className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-900/30 rounded-lg transition-colors"
            >
              <IconTrash />
            </button>
          </div>

          {/* Entries */}
          <div className="divide-y divide-gray-700/40">
            {topic.entries.map((entry, ei) => (
              <div key={ei} className="flex items-start gap-3 px-4 py-3">
                <span className="mt-2 text-xs text-gray-600 font-mono w-5 flex-shrink-0 text-center">{ei + 1}</span>
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={entry.label}
                    onChange={e => updateEntry(ti, ei, 'label', e.target.value.slice(0, 30))}
                    placeholder="Rule title"
                    maxLength={30}
                    className="bg-gray-900/60 border border-gray-600 rounded-lg px-3 py-1.5 text-sm text-gray-200 font-medium focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-600"
                  />
                  <input
                    type="text"
                    value={entry.content}
                    onChange={e => updateEntry(ti, ei, 'content', e.target.value.slice(0, 300))}
                    placeholder="Description (optional)"
                    maxLength={300}
                    className="bg-gray-900/60 border border-gray-600 rounded-lg px-3 py-1.5 text-sm text-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-600"
                  />
                </div>
                <button
                  onClick={() => removeEntry(ti, ei)}
                  className="mt-1 p-1.5 text-gray-600 hover:text-red-400 hover:bg-red-900/30 rounded-lg transition-colors flex-shrink-0"
                >
                  <IconTrash />
                </button>
              </div>
            ))}

            {topic.entries.length < 15 && (
              <button
                onClick={() => addEntry(ti)}
                className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-gray-500 hover:text-blue-400 hover:bg-blue-900/10 transition-colors"
              >
                <IconPlus /><span>{t ? t('settings_add_rule') : 'Add rule'}</span>
              </button>
            )}
            {topic.entries.length === 0 && (
              <div className="px-4 py-3 text-sm text-gray-600 italic">{t ? t('settings_no_rules_yet') : 'No rules yet — click "Add rule" above.'}</div>
            )}
          </div>
        </div>
      ))}

      {topics.length === 0 && (
        <div className="bg-gray-800/40 border border-dashed border-gray-700 rounded-2xl p-8 text-center">
          <div className="text-3xl mb-3">📜</div>
          <p className="text-gray-400 text-sm">{t ? t('settings_no_topics_yet') : 'No rule topics defined yet — use the button below to add one.'}</p>
        </div>
      )}

      {topics.length < 10 && (
        <button
          onClick={addTopic}
          className="flex items-center justify-center gap-2 w-full py-3 text-sm text-gray-400 hover:text-blue-400 border border-dashed border-gray-700 hover:border-blue-500/50 hover:bg-blue-900/10 rounded-2xl transition-colors"
        >
          <IconPlus /><span>{t ? t('settings_add_topic') : 'Add Topic'}</span>
          <span className="text-xs text-gray-600 ml-1">({topics.length}/10)</span>
        </button>
      )}
      {topics.length >= 10 && (
        <div className="flex items-center justify-center gap-2 w-full py-3 text-xs text-gray-600 border border-dashed border-gray-700/50 rounded-2xl">
          {t ? t('settings_max_topics') : 'Maximum of 10 topics reached'}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Discord Settings
// ---------------------------------------------------------------------------
function DiscordTab({ slug, onToast, t }) {
  const queryClient = useQueryClient();
  const [discord, setDiscord] = useState(null);
  const [dirty, setDirty] = useState(false);

  const { data: savedDiscord, isLoading } = useQuery({
    queryKey: ['settings_discord', slug],
    queryFn: () => apiGet(`/v1/clan/${slug}/settings/discord`, { withAuth: true }),
    staleTime: 30_000,
  });

  const { data: botStatus, refetch: refetchStatus } = useQuery({
    queryKey: ['settings_discord_status', slug],
    queryFn: () => apiGet(`/v1/clan/${slug}/settings/discord/status`, { withAuth: true }),
    staleTime: 60_000,
    enabled: !!savedDiscord?.guild_id,
  });

  useEffect(() => {
    if (savedDiscord && !dirty) setDiscord({ ...savedDiscord });
  }, [savedDiscord]);

  const mutation = useMutation({
    mutationFn: (data) => apiPut(`/v1/clan/${slug}/settings/discord`, data, { withAuth: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings_discord', slug] });
      queryClient.invalidateQueries({ queryKey: ['settings_discord_status', slug] });
      queryClient.invalidateQueries({ queryKey: ['clans_list'] });
      setDirty(false);
      onToast('Discord settings saved!', 'success');
    },
    onError: (err) => onToast(`Save failed: ${err.message}`, 'error'),
  });

  const [showHelp, setShowHelp] = useState(false);

  const set = useCallback((key, val) => {
    setDiscord(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  }, []);

  if (isLoading || !discord) {
    return <div className="flex items-center justify-center h-48 text-gray-400">{t ? t('loading') : 'Loading…'}</div>;
  }

  const hasGuildId = !!discord.guild_id;
  const status = botStatus?.bot_status;
  const isConnected = status === 'connected';
  const isNotInGuild = hasGuildId && (status === 'not_in_guild' || status === 'no_token');

  let bannerClass, bannerTitle, bannerDesc, dotClass;
  if (isConnected) {
    bannerClass = 'bg-green-900/20 border-green-700/50 text-green-300';
    bannerTitle = `Bot connected to ${botStatus.guild_name || 'server'}`;
    bannerDesc = `Server ID: ${discord.guild_id}`;
    dotClass = 'bg-green-400 shadow-lg shadow-green-900/50';
  } else if (isNotInGuild) {
    bannerClass = 'bg-yellow-900/20 border-yellow-700/50 text-yellow-300';
    bannerTitle = 'Bot is not in this server';
    bannerDesc = <span>Invite the bot to your Discord server using the OAuth2 link — see <button onClick={() => setShowHelp(true)} className="underline underline-offset-2 hover:opacity-80 transition-opacity">How to setup Discord</button>.</span>;
    dotClass = 'bg-yellow-400 shadow-lg shadow-yellow-900/50';
  } else if (hasGuildId && status === 'unknown') {
    bannerClass = 'bg-gray-800/60 border-gray-700 text-gray-400';
    bannerTitle = 'Could not verify bot status';
    bannerDesc = `Server ID: ${discord.guild_id} — status check failed${botStatus?.debug ? ` (${botStatus.debug})` : ''}, try again later.`;
    dotClass = 'bg-gray-500';
  } else if (hasGuildId && !status) {
    bannerClass = 'bg-gray-800/60 border-gray-700 text-gray-400';
    bannerTitle = 'Checking bot status…';
    bannerDesc = `Server ID: ${discord.guild_id}`;
    dotClass = 'bg-gray-500 animate-pulse';
  } else {
    bannerClass = 'bg-gray-800/60 border-gray-700 text-gray-400';
    bannerTitle = 'Discord bot not configured';
    bannerDesc = 'Enter a Server ID below to enable Discord integration';
    dotClass = 'bg-gray-600';
  }

  return (
    <div className="space-y-5">
      {showHelp && <DiscordHelpModal onClose={() => setShowHelp(false)} />}

      {/* Help Button */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowHelp(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-600/30 text-blue-400 rounded-lg text-sm font-medium transition-colors"
        >
          <IconHelp />
          {t ? t('settings_discord_setup_guide') : 'How to setup Discord'}
        </button>
      </div>

      {/* Status Banner */}
      <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${bannerClass}`}>
        <IconDiscord />
        <div className="flex-1">
          <div className="text-sm font-semibold">{bannerTitle}</div>
          <div className="text-xs opacity-70">{bannerDesc}</div>
        </div>
        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dotClass}`} />
      </div>

      {/* Server Configuration */}
      <SectionCard title={t ? t('settings_discord_server_config') : 'Server Configuration'} icon="🖥️" action={
        <SaveButton onClick={() => mutation.mutate(discord)} loading={mutation.isPending} dirty={dirty} />
      }>
        <TextInput
          label={t ? t('settings_discord_guild_id') : 'Discord Server ID (Guild ID)'}
          value={discord.guild_id}
          onChange={v => set('guild_id', v.replace(/\D/g, '').slice(0, 20))}
          placeholder="123456789012345678"
          monospace
          maxLength={20}
          validate={validateSnowflake}
        />
        <TextInput
          label={t ? t('settings_discord_invite_link') : 'Discord Invite Link'}
          value={discord.invite_link}
          onChange={v => set('invite_link', v.slice(0, 200))}
          placeholder="https://discord.gg/yourserver"
          maxLength={200}
          validate={validateInviteLink}
        />
        <SelectInput
          label={t ? t('settings_discord_bot_language') : 'Bot Language'}
          value={discord.locale}
          onChange={v => set('locale', v)}
          options={[
            { value: 'de', label: 'Deutsch' },
            { value: 'en', label: 'English' },
          ]}
        />
      </SectionCard>

      {/* Channels */}
      <SectionCard title={t ? t('settings_discord_channels') : 'Discord Channels'} icon="💬">
        <TextInput
          label={t ? t('settings_discord_welcome_channel') : 'Welcome Channel'}
          value={discord.link_channel_id}
          onChange={v => set('link_channel_id', v.replace(/\D/g, '').slice(0, 20))}
          placeholder="Channel ID"
          monospace
          maxLength={20}
          validate={validateSnowflake}
        />
        <TextInput
          label={t ? t('settings_discord_reminder_channel') : 'War Reminder Channel'}
          value={discord.reminder_channel_id}
          onChange={v => set('reminder_channel_id', v.replace(/\D/g, '').slice(0, 20))}
          placeholder="Channel ID"
          monospace
          maxLength={20}
          validate={validateSnowflake}
        />
        <TextInput
          label={t ? t('settings_discord_hof_channel') : 'Hall of Fame Channel'}
          value={discord.hof_channel_id}
          onChange={v => set('hof_channel_id', v.replace(/\D/g, '').slice(0, 20))}
          placeholder="Channel ID"
          monospace
          maxLength={20}
          validate={validateSnowflake}
        />
        <TextInput
          label={t ? t('settings_discord_admin_channel') : 'Admin Channel'}
          value={discord.admin_channel_id}
          onChange={v => set('admin_channel_id', v.replace(/\D/g, '').slice(0, 20))}
          placeholder="Channel ID"
          monospace
          maxLength={20}
          validate={validateSnowflake}
        />
      </SectionCard>

      {/* Roles */}
      <SectionCard title={t ? t('settings_discord_roles') : 'Bot Roles'} icon="🎭">
        <TextInput
          label={t ? t('settings_discord_member_role') : 'Member Role ID'}
          value={discord.member_role_id}
          onChange={v => set('member_role_id', v.replace(/\D/g, '').slice(0, 20))}
          placeholder="Role ID"
          monospace
          maxLength={20}
          validate={validateSnowflake}
        />
      </SectionCard>

      {/* Notifications */}
      <SectionCard title={t ? t('settings_discord_notifications') : 'Notification Settings'} icon="🔔">
        <Toggle
          checked={discord.war_reminder_enabled !== false}
          onChange={v => set('war_reminder_enabled', v)}
          label={t ? t('settings_discord_war_reminders') : 'War Attack Reminders'}
        />
        <Toggle
          checked={discord.hof_notification_enabled !== false}
          onChange={v => set('hof_notification_enabled', v)}
          label={t ? t('settings_discord_hof_notif') : 'Hall of Fame Notifications'}
        />
        <Toggle
          checked={discord.member_bonus_enabled !== false}
          onChange={v => set('member_bonus_enabled', v)}
          label={t ? t('settings_discord_member_bonus') : 'Discord Member Bonus'}
        />
      </SectionCard>

      <div className="flex justify-end">
        <SaveButton onClick={() => mutation.mutate(discord)} loading={mutation.isPending} dirty={dirty} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main SettingsPage component
// ---------------------------------------------------------------------------
export default function SettingsPage({ slug, t: tProp, lang, setLang }) {
  const { isAuthenticated, isAdmin, loading: authLoading, profileLoaded } = useAuth();
  const { t: tInternal, lang: langInternal, setLang: setLangInternal } = useTranslation();
  const t = tProp || tInternal;
  const effectiveLang = lang || langInternal;
  const effectiveSetLang = setLang || setLangInternal;

  const TABS = [
    { id: 'scoring', label: t('settings_tab_scoring'), icon: '📊' },
    { id: 'rules', label: t('settings_tab_rules'), icon: '📜' },
    { id: 'discord', label: 'Discord', icon: '🎮' },
  ];

  const [activeTab, setActiveTabState] = useState(() => {
    const saved = localStorage.getItem(`settingsTab_${slug}`);
    return ['scoring', 'rules', 'discord'].includes(saved) ? saved : 'scoring';
  });
  const [toast, setToast] = useState(null);

  const setActiveTab = useCallback((id) => {
    setActiveTabState(id);
    localStorage.setItem(`settingsTab_${slug}`, id);
  }, [slug]);

  const onToast = useCallback((message, type) => {
    setToast({ message, type });
  }, []);

  if (authLoading || !profileLoaded) {
    return (
      <div id="settingsPage">
        <h1 className="text-2xl md:text-3xl mb-6">⚙️ <span>{t('settings_title')}</span></h1>
        <div className="flex items-center justify-center h-48 text-gray-400">{t('loading')}</div>
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin) {
    return (
      <div id="settingsPage">
        <h1 className="text-2xl md:text-3xl mb-6">⚙️ <span>{t('settings_title')}</span></h1>
        <div className="bg-gray-800/60 border border-gray-700 rounded-2xl p-10 text-center">
          <div className="text-4xl mb-4">🔒</div>
          <h2 className="text-lg font-semibold text-gray-200 mb-2">{t('settings_access_required')}</h2>
          <p className="text-gray-400 text-sm">{t('settings_access_desc')}</p>
        </div>
      </div>
    );
  }

  return (
    <div id="settingsPage">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onDismiss={() => setToast(null)}
        />
      )}

      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl flex items-center gap-3">
          <span>⚙️</span>
          <span>{t('settings_title')}</span>
          <span className="text-base font-normal text-gray-500 ml-1">— {t('settings_subtitle')}</span>
        </h1>
      </div>

      {/* Tab Bar */}
      <div className="flex gap-1 bg-gray-800/60 border border-gray-700 p-1 rounded-xl mb-6 w-fit">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/30'
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/60'
            }`}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'scoring' && <ScoringTab slug={slug} onToast={onToast} t={t} />}
      {activeTab === 'rules' && <RulesTab slug={slug} onToast={onToast} t={t} />}
      {activeTab === 'discord' && <DiscordTab slug={slug} onToast={onToast} t={t} />}
    </div>
  );
}
